import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge, Card, EmptyState, ErrorAlert, PageHeader, Pagination, Spinner, StatCard, StatusBadge } from '../components/ui';
import { formatDate, truncate } from '@kontentowo/shared';

export function DashboardPage() {
  const { data, error, loading } = useAsync(() => api.stats(), []);
  const providers = useAsync(() => api.providers(), []);
  const costs = useAsync(() => api.costSummary(), []);
  const publishRecent = useAsync(() => api.publishRecent(50), []);
  const [recentPage, setRecentPage] = useState(1);
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [publishingPage, setPublishingPage] = useState(1);
  const [recentBrand, setRecentBrand] = useState('ALL');
  const [upcomingBrand, setUpcomingBrand] = useState('ALL');
  const pageSize = 8;

  const brandOptions = data
    ? [...new Set([
      ...data.recentContent.map((item) => item.brand?.name).filter((name): name is string => Boolean(name)),
      ...data.upcoming.map((item) => item.brandName).filter(Boolean),
    ])].sort((a, b) => a.localeCompare(b))
    : [];
  const filteredRecentContent = data?.recentContent.filter((item) => recentBrand === 'ALL' || item.brand?.name === recentBrand) ?? [];
  const filteredUpcoming = data?.upcoming.filter((item) => upcomingBrand === 'ALL' || item.brandName === upcomingBrand) ?? [];

  return (
    <>
      <PageHeader title="Dashboard" subtitle="Przegląd pracy Kontentowo: statusy treści, ostatnia aktywność i najbliższe publikacje." />
      {loading && <Spinner />}
      {error && <ErrorAlert error={error} />}
      {data && (
        <div className="stack">
          <div className="grid grid--stats">
            <StatCard value={data.brands} label="Brands" />
            <StatCard value={data.drafts} label="Drafts" />
            <StatCard value={data.review} label="Waiting for review" />
            <StatCard value={data.approved} label="Approved" />
            <StatCard value={data.scheduled} label="Scheduled" />
            <StatCard value={data.published} label="Published" />
            <StatCard value={data.failed} label="Failed" />
          </div>

          <div className="grid grid--2">
            <Card
              title="Recent Content"
              actions={<select className="select" value={recentBrand} onChange={(event) => { setRecentBrand(event.target.value); setRecentPage(1); }} aria-label="Filtr Recent Content po brandzie"><option value="ALL">Wszystkie brandy</option>{brandOptions.map((brand) => <option key={brand} value={brand}>{brand}</option>)}</select>}
              flush
            >
              {filteredRecentContent.length === 0 ? (
                <EmptyState title="Brak treści" hint={recentBrand === 'ALL' ? 'Wygeneruj pierwszy content w Content Studio.' : 'Brak treści dla wybranego brandu.'} />
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Temat</th>
                      <th>Brand</th>
                      <th>Status</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecentContent.slice((recentPage - 1) * pageSize, recentPage * pageSize).map((item) => (
                      <tr key={item.id}>
                        <td>
                          <Link to={`/content/${item.id}`}>{truncate(item.topic, 48)}</Link>
                        </td>
                        <td className="muted">{item.brand?.name ?? '—'}</td>
                        <td>
                          <StatusBadge status={item.status} />
                        </td>
                        <td className="muted">{formatDate(item.updatedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <Pagination page={recentPage} pageSize={pageSize} total={filteredRecentContent.length} onChange={setRecentPage} />
            </Card>

            <Card
              title="Upcoming Content"
              actions={<select className="select" value={upcomingBrand} onChange={(event) => { setUpcomingBrand(event.target.value); setUpcomingPage(1); }} aria-label="Filtr Upcoming Content po brandzie"><option value="ALL">Wszystkie brandy</option>{brandOptions.map((brand) => <option key={brand} value={brand}>{brand}</option>)}</select>}
              flush
            >
              {filteredUpcoming.length === 0 ? (
                <EmptyState title="Brak zaplanowanych treści" hint={upcomingBrand === 'ALL' ? 'Zaplanuj zatwierdzony content w Calendar.' : 'Brak publikacji dla wybranego brandu.'} />
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Kiedy</th>
                      <th>Temat</th>
                      <th>Brand</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUpcoming.slice((upcomingPage - 1) * pageSize, upcomingPage * pageSize).map((s) => (
                      <tr key={s.id}>
                        <td>{formatDate(s.scheduledAt)}</td>
                        <td>
                          <Link to={`/content/${s.contentId}`}>{truncate(s.topic, 36)}</Link>
                        </td>
                        <td className="muted">{s.brandName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <Pagination page={upcomingPage} pageSize={pageSize} total={filteredUpcoming.length} onChange={setUpcomingPage} />
            </Card>
          </div>

          <div className="grid grid--2">
            <Card title="AI usage">
              {costs.loading && <Spinner />}
              {costs.error ? <ErrorAlert error={costs.error} /> : null}
              {costs.data && (
                <div className="stack">
                  <div className="grid grid--stats" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))' }}>
                    <StatCard value={`$${costs.data.totalCost.toFixed(5)}`} label="Koszt AI" />
                    <StatCard value={costs.data.totalGenerations} label="Generacji" />
                  </div>
                  <div className="pill-list">
                    {costs.data.byProvider.map((p) => (
                      <Badge key={p.providerKey} tone="neutral">
                        {p.providerKey}: {p.count} · ${p.cost.toFixed(5)}
                      </Badge>
                    ))}
                  </div>
                  <div className="btn-row">
                    <Link className="btn btn--sm" to="/costs">
                      Koszty →
                    </Link>
                  </div>
                </div>
              )}
            </Card>

            <Card title="Publishing status">
              {providers.loading && <Spinner />}
              {providers.error ? <ErrorAlert error={providers.error} /> : null}
              {providers.data && (
                <div className="stack">
                  <div className="pill-list">
                    {providers.data.publishers.map((p) => (
                      <Badge key={p.type} tone={p.mock ? 'warning' : 'success'}>
                        {p.type}: {p.mock ? 'mock' : 'LIVE'}
                      </Badge>
                    ))}
                  </div>
                  {publishRecent.data && publishRecent.data.length > 0 && (
                    <>
                      <div style={{ fontWeight: 650, fontSize: 13 }}>Ostatnie publikacje</div>
                      <table className="table" style={{ marginTop: 4 }}>
                        <thead>
                          <tr>
                            <th>Platforma</th>
                            <th>Temat</th>
                            <th>Status / powód</th>
                          </tr>
                        </thead>
                        <tbody>
                          {publishRecent.data.slice((publishingPage - 1) * pageSize, publishingPage * pageSize).map((r) => (
                            <tr key={r.id}>
                              <td>
                                <Badge tone="neutral">{r.platform}</Badge>
                              </td>
                              <td>
                                <Link to={`/content/${r.contentId}`} title={r.brandName}>
                                  {truncate(r.topic, 32)}
                                </Link>
                              </td>
                              <td>
                                {r.status === 'SUCCESS' ? (
                                  <Badge tone="success">ok</Badge>
                                ) : (
                                  <span className="muted" style={{ fontSize: 12 }} title={r.message ?? ''}>
                                    <Badge tone="danger">{r.status}</Badge> {truncate(r.message ?? 'brak powodu', 42)}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <Pagination page={publishingPage} pageSize={pageSize} total={publishRecent.data.length} onChange={setPublishingPage} />
                    </>
                  )}
                  {publishRecent.data && publishRecent.data.length === 0 && <p className="muted">Brak publikacji.</p>}
                </div>
              )}
            </Card>
          </div>
        </div>
      )}
    </>
  );
}
